import { Code2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { LANGUAGE_LABEL, lineCountLabel, previewLine } from "@/pages/multi-agent/ide/attachments";
import type { MultiAgentAttachment } from "@/services/multi-agent/api";

/**
 * A big paste, in the composer, before it is sent.
 *
 * ── WHAT THIS REPLACED, AND WHY IT WAS NOT A STYLING PROBLEM ────────────────────────────────────
 *
 * A pasted block became an attachment and then rendered through the composer's GENERIC chip loop,
 * whose icon ternary has branches for `pdf`, `audio`, `url` and `image` and none for `code` — so it
 * fell through to `FileText` and drew as a plain document, pixel-identical to an uploaded PDF. Its
 * label was the literal string "pasted code". Reported, and all of it downstream of one mistake:
 * pasted code was MODELLED as a file attachment, so it was rendered as one.
 *
 * It is not a file. It has no name, no path and nothing on disk; it is a block of text the reader
 * chose to hand over, and the three things they need to know about it are what language it is, how
 * much of it there is, and what it starts with. This says those three, in one row.
 *
 * ── THE ONE ROW IS THE BUDGET ───────────────────────────────────────────────────────────────────
 *
 * "Do not make the composer significantly taller just to show the pasted code" is a hard constraint,
 * so the preview is a SINGLE line of the paste's own first meaningful line (`previewLine` skips a
 * lone opening brace), truncated with an ellipsis and never wrapped. Anything more is what the
 * right-hand panel is for — which is where the whole card leads.
 *
 * ── WHY THE WHOLE CARD IS THE BUTTON ────────────────────────────────────────────────────────────
 *
 * Same rule as `ReferenceBlock` in the transcript: one affordance, one tab stop, a touch target
 * that clears §8.5's floor without a separate "view" link competing with the card that contains it.
 * Remove stays its own control because it is a different, destructive action — and it is a sibling
 * rather than a nested `<button>`, which is invalid HTML and is why this is a `<div>` wrapper with
 * two buttons rather than a button containing one.
 */
export interface PastedCodeChipProps {
  attachment: MultiAgentAttachment;
  /** Open it in the workspace, editable. Omitted, the card is informational and not clickable. */
  onOpen?: (attachment: MultiAgentAttachment) => void;
  /** The workspace is currently open on THIS paste. */
  active?: boolean;
  onRemove: (id: string) => void;
}

export function PastedCodeChip({ attachment, onOpen, active, onRemove }: PastedCodeChipProps) {
  const body = attachment.content ?? "";
  const language = attachment.language ?? "";
  // `LANGUAGE_LABEL` is the display name for a language this client actually detected; an
  // undetected paste says "Code" and claims nothing about what it is (see `detectCodeLanguage`).
  const label = language ? LANGUAGE_LABEL[language] ?? language : "Code";
  const preview = previewLine(body);

  return (
    <div
      data-pasted-code={attachment.id}
      className={cn(
        "flex min-w-0 max-w-full items-center gap-2 rounded-md border bg-[var(--ma-surface-soft)] py-1 pl-2 pr-1 transition-colors motion-reduce:transition-none",
        active ? "border-primary/45" : "border-border",
      )}
    >
      <Code2 aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      {onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(attachment)}
          aria-expanded={!!active}
          /*
           * The accessible name says the ACTION and the SUBJECT — "Pasted code" alone would read
           * identically on two different pastes, and a reader with two of them open needs to know
           * which one this control edits.
           */
          aria-label={`${active ? "Close" : "Open"} pasted ${label}, ${lineCountLabel(body)}`}
          className="flex min-w-0 flex-1 items-baseline gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="shrink-0 text-xs font-medium text-foreground">{label}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {lineCountLabel(body)}
          </span>
          {preview && (
            /*
             * The paste's own first line, monospace so it reads as code at a glance, and hidden on a
             * narrow composer where the language and the size are the facts worth the width.
             */
            <span className="hidden min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground sm:block">
              {preview}
            </span>
          )}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-baseline gap-2">
          <span className="shrink-0 text-xs font-medium text-foreground">{label}</span>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            {lineCountLabel(body)}
          </span>
        </span>
      )}
      <button
        type="button"
        onClick={() => onRemove(attachment.id)}
        title="Remove pasted code"
        aria-label={`Remove pasted ${label}`}
        className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      >
        <X aria-hidden className="h-3 w-3" />
      </button>
    </div>
  );
}
